import { ChevronDownIcon } from "@radix-ui/react-icons";
import {
  Badge,
  Box,
  Card,
  Flex,
  Grid,
  Skeleton,
  Text,
} from "@radix-ui/themes";
import { ComponentPropsWithoutRef, ReactNode } from "react";
import { PublicationStatus } from "./client";

// Order matches the editorial workflow and the status summary row.
export const STATUS_ORDER: PublicationStatus[] = [
  "draft",
  "in_review",
  "correction_required",
  "published",
];

export const statusLabels: Record<PublicationStatus, string> = {
  draft: "Draft",
  in_review: "In review",
  correction_required: "Correction required",
  published: "Published",
};

const statusColors: Record<
  PublicationStatus,
  "gray" | "amber" | "red" | "teal"
> = {
  draft: "gray",
  in_review: "amber",
  correction_required: "red",
  published: "teal",
};

export function StatusBadge({ status }: { status: PublicationStatus }) {
  return (
    <Badge color={statusColors[status]} size="1" variant="soft">
      {statusLabels[status]}
    </Badge>
  );
}

export function BrandMark() {
  return (
    <Flex align="center" gap="3">
      <img
        alt=""
        className="gw-logo"
        height={32}
        src={`${process.env.PUBLIC_URL}/geen-wines-logo.jpg`}
        width={32}
      />
      <Flex align="baseline" gap="2">
        <Text size="3" weight="bold" wrap="nowrap">
          Geen Wines
        </Text>
        <Box display={{ initial: "none", xs: "block" }}>
          <Text color="gray" size="2" wrap="nowrap">
            Editorial
          </Text>
        </Box>
      </Flex>
    </Flex>
  );
}

export function TopBar({ children }: { children?: ReactNode }) {
  return (
    <header className="gw-topbar">
      <Flex align="center" className="gw-topbar-inner" gap="4" justify="between">
        <BrandMark />
        {children}
      </Flex>
    </header>
  );
}

// Labels sit above their control; help and error text sit below it.
export function Field({
  children,
  htmlFor,
  label,
}: {
  children: ReactNode;
  htmlFor: string;
  label: string;
}) {
  return (
    <Flex direction="column" gap="1" minWidth="0">
      <Text as="label" htmlFor={htmlFor} size="2" weight="medium">
        {label}
      </Text>
      {children}
    </Flex>
  );
}

// Radix Themes has no native select. A native one keeps keyboard, screen
// reader and mobile behaviour, so it is styled to match the text fields.
export function NativeSelect(props: ComponentPropsWithoutRef<"select">) {
  return (
    <div className="gw-select-wrap">
      <select {...props} className="gw-select" />
      <ChevronDownIcon aria-hidden className="gw-select-icon" />
    </div>
  );
}

export function SummarySkeleton() {
  return (
    <Grid columns={{ initial: "2", sm: "4" }} gap="3">
      {STATUS_ORDER.map((status) => (
        <Card key={status} size="2">
          <Skeleton height="16px" width="60%" />
          <Box mt="3">
            <Skeleton height="28px" width="32px" />
          </Box>
        </Card>
      ))}
    </Grid>
  );
}

export function QueueSkeleton() {
  return (
    <ul aria-hidden className="gw-queue">
      {[0, 1, 2, 3].map((row) => (
        <li key={row}>
          <Flex align="center" gap="4" justify="between" py="3">
            <Flex direction="column" gap="2" flexGrow="1">
              <Flex align="center" gap="2">
                <Skeleton height="18px" width="40%" />
                <Skeleton height="18px" width="64px" />
              </Flex>
              <Skeleton height="14px" width="55%" />
            </Flex>
            <Skeleton height="32px" width="120px" />
          </Flex>
        </li>
      ))}
    </ul>
  );
}
